import React, { useEffect } from 'react';
import AgTable from '@navi/web-ui/lib/components/AgTable/AgTable';
import { ModalDialog, Typography } from '@navi/web-ui/lib/primitives';
import { AnswerType, AnswerView } from '../../interfaces/CommunicationHistory.type';
import { formatAmount } from '../../../../utils/commonUtils';
import styles from './../index.module.scss';
import ViewIcon from 'src/assets/icons/ViewIcon';
import { FIELD_FEEBACK_TYPES } from '../constants';
import MapPinpoint from 'src/assets/icons/MapPinpoint';
import { DateFormat, dateFormat, formatFeedbackTime, utcToIST } from '@cp/utils/DateHelper';
import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import CallDetailsViewFeedback from './CallDetailsViewFeedback';
import dayjs from 'dayjs';
import { DateTimeFormat } from '@cp/src/components/DateTimePicker/constants';

interface ViewFeedbackFormProps {
  open: boolean;
  onClose: () => void;
  answers: AnswerView[];
  comments: string;
  modalType?: string;
  gMapLocationUrl?: string;
  interactionData: any;
}

const columnDefs = [
  {
    field: 'question',
    cellStyle: { paddingTop: '16px', paddingBottom: '16px', lineHeight: '24px' }
  },
  {
    field: 'answer',
    cellStyle: { paddingTop: '16px', paddingBottom: '16px', lineHeight: '24px' },
    cellRenderer: (params: ICellRendererParams) => {
      return <div className={styles.answerViewCell}>{params.value}</div>;
    }
  }
];

type RowData = {
  question?: string;
  answer?: string;
};

const ViewFeedbackForm: React.FC<ViewFeedbackFormProps> = props => {
  const {
    open,
    onClose,
    answers,
    comments,
    modalType,
    gMapLocationUrl = `http://maps.google.com/?q=`,
    interactionData
  } = props;

  const [images, setImages] = React.useState<Array<string>>([]);
  const [rowData, setRowData] = React.useState<Array<RowData>>([]);
  const [openModal, setOpenModal] = React.useState<string>();
  const handleImageExtraction = (imagesUrls: string | undefined) => {
    if (!imagesUrls) return;
    // assuming MULTI_IMAGE_UPLOAD i will get stringified array of urls
    const imageUrls = JSON.parse(imagesUrls);
    setImages(prev => [...prev, ...imageUrls]);
  };

  const interactionFilledBy = interactionData?.metadata?.filled_by;
  const interactionFilledAt = interactionData?.metadata?.filled_at;

  const callStartTime = Number(interactionData?.metadata?.START_TIMESTAMP_MS);
  const assignedTo = interactionData?.metadata?.assigned_to;

  const dateTimeStamp = interactionData?.metadata?.startTime;

  useEffect(() => {
    const tempRowData: Array<RowData> = [];
    for (let i = 0; i < answers.length; i++) {
      if (answers[i].answerType === AnswerType.MULTI_IMAGE_UPLOAD) {
        handleImageExtraction(answers[i].inputText);
        continue;
      }
      if (answers[i].answerType === AnswerType.MULTI_AUDIO_UPLOAD) {
        continue;
      }
      const data: RowData = {
        question: answers[i].questionName,
        answer:
          answers[i].answerType === AnswerType.OPTIONS ||
          answers[i].answerType === AnswerType.GROUPED_OPTIONS
            ? answers[i].optionText
            : answers[i].answerType === AnswerType.RADIO_OPTIONS
            ? answers[i].optionText
            : answers[i].answerType === AnswerType.CHECKBOX_OPTIONS
            ? answers[i].optionText
            : answers[i].answerType === AnswerType.TIME
            ? formatFeedbackTime(answers[i].inputText as string)
            : answers[i].answerType === AnswerType.DATE
            ? dateFormat(
                new Date(answers[i]?.inputDate as string),
                DateFormat.LONG_DATE_FORMAT_WITHOUT_TIME
              )
            : answers[i].answerType === AnswerType.AMOUNT
            ? formatAmount(answers[i].inputAmount ?? 0, false)
            : answers[i].inputText
      };
      tempRowData.push(data);
    }
    if (interactionFilledBy) {
      tempRowData?.push({
        question: 'Feedback filled by',
        answer: interactionFilledBy
      });
    }

    if (interactionFilledAt) {
      tempRowData?.push({
        question: 'Feedback filled at',
        answer: dateFormat(
          new Date(utcToIST(interactionFilledAt)),
          DateFormat.LONG_DATE_FORMAT_WITH_TIME
        )
      });
    }

    if (comments) {
      tempRowData.push({
        question: 'Comments',
        answer: comments
      });
    }

    if (assignedTo) {
      tempRowData.push({
        question: 'Assigned to',
        answer: assignedTo
      });
    }
    if (callStartTime) {
      tempRowData.push({
        question: 'Call start time',
        answer: dayjs(callStartTime).format(DateTimeFormat.DATE_TIME)
      });
    }

    setRowData(tempRowData as any);
  }, []);

  const handleClose = () => {
    setOpenModal(undefined);
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      header={
        modalType === FIELD_FEEBACK_TYPES.FIELD_FEEDBACK
          ? FIELD_FEEBACK_TYPES.FEEDBACK_DETAILS
          : FIELD_FEEBACK_TYPES.VIEW_FEEDBACK
      }
      contentClasses="pb-4"
      style={{
        width: '800px',
        height: '600px'
      }}
    >
      <div>
        {interactionData?.metadata?.vendor === 'AIRTEL' ? (
          <CallDetailsViewFeedback interactionData={interactionData} />
        ) : null}

        {modalType === FIELD_FEEBACK_TYPES.FIELD_FEEDBACK && (
          <div className={styles.feedbackLocation}>
            <div className={styles.locHeadingWrapper}>
              <div>{FIELD_FEEBACK_TYPES.LOCATION_OF_FEEDBACK}</div>
              <MapPinpoint />
            </div>
            {gMapLocationUrl && (
              <a
                href={gMapLocationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.gMapUrlStyles}
              >
                {gMapLocationUrl}
              </a>
            )}
          </div>
        )}
        <div className="h-[250px]">
          <>
            <Typography className={'mb-3'} variant="h4">
              Feedback
            </Typography>
            <AgTable
              defaultColDef={{
                suppressMovable: true,
                wrapText: true,
                autoHeight: true
              }}
              gridOptions={{ suppressCellSelection: true }}
              columnDefs={columnDefs}
              rowData={rowData}
              sizeColumnsToFit
            />
          </>
        </div>

        {images?.length ? (
          <div className={styles.imageDisplayOuterContainer}>
            <Typography className={styles.heading} variant="h4">
              Screenshot(s)
            </Typography>
            <div className={styles.imageContainer}>
              {images?.map((image, index) => {
                return (
                  <div
                    onClick={() => setOpenModal(image.preSignedUri)}
                    className={styles.imagePlaceholderContainer}
                    key={image.preSignedUri}
                  >
                    <img className={styles.image} src={image?.preSignedUri} key={index} />
                    <div className={styles.overlay} />
                    <ViewIcon className={styles.icon} fillColor="#ffffff" />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <ModalDialog open={Boolean(openModal)} onClose={handleClose} className={styles.imageModal}>
          <img key={openModal} className={styles.mediaHeightFit} src={openModal} alt="" />
        </ModalDialog>
      </div>
    </ModalDialog>
  );
};

export default ViewFeedbackForm;
